import { FormDrawerContent, FormFetchDrawer, Id, RenderUpdateDrawerProps, useLaravelCrudEntity } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DataFieldIndexResource, DataFieldType } from "../../../../shared/types/dataField";
import { birthdayWidgetService } from "../../dashboard/widgets/birthdays/service";
import { contactService } from "../../shared/api/contactService";
import { invitationListKeyItem } from "../../shared/api/invitationListService";
import { IContactUpdateFormState, IContactUpdateResource } from "../../shared/types";
import { ContactAttributeField } from "../ContactAttributeField";

export const ContactUpdateDrawer: FC<
	RenderUpdateDrawerProps & {
		dataFields: DataFieldIndexResource[];
		contactViewId: Id;
	}
> = ({ dataFields, contactViewId, ...drawerProps }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { updateEntity } = useLaravelCrudEntity(contactService, {
		entityName: t("Contact"),
		messages: {
			create: t("Contact was saved."),
			update: t("Contact was saved."),
			delete: t("Contact was deleted."),
		},
	});

	const validate = useCallback(
		(values: IContactUpdateFormState) => {
			const errors: { [K in keyof IContactUpdateFormState]?: any } = {};

			values.attributes.forEach((attribute, idx) => {
				if (attribute.type === "dataField") {
					const dataField = dataFields.find((d) => d.id === attribute.entityType);

					switch (dataField?.type) {
						case DataFieldType.TEXT:
							if (values.attributes[idx]?.value && values.attributes[idx].value.length > 181) {
								errors.attributes[idx].value =
									`${dataField.label} ${t("must not be greater than {{max}} characters.", { max: 181 })}`;
							}
							break;
						case DataFieldType.PHONE:
							if (values.attributes[idx]?.value && values.attributes[idx].value.length > 20) {
								errors.attributes[idx].value =
									`${dataField.label} ${t("must not be greater than {{max}} characters.", { max: 20 })}`;
							}
							break;
					}
				}
			});

			return errors;
		},
		[t, dataFields],
	);

	const createInitialValues = (entityResource: IContactUpdateResource): IContactUpdateFormState => {
		const attributes = entityResource.attributes.filter(
			(attribute) =>
				attribute.entityType !== "locale" &&
				attribute.entityType !== "tenants" &&
				attribute.entityType !== "contactType",
		);
		return {
			attributes: attributes,
		};
	};

	return (
		<FormFetchDrawer
			{...drawerProps}
			title={t("Edit contact")}
			queryFn={() =>
				contactService.show(drawerProps.id, `${contactViewId}`).then((x) => createInitialValues(x.data.data))
			}
			queryKey={[...contactService.showKey(drawerProps.id), `${contactViewId}`, "updateDrawer"]}
			onSubmit={async (values: IContactUpdateFormState) => {
				const result = await updateEntity(drawerProps.id, {
					attributes: values.attributes.map((x) => ({
						id: x.id,
						value: x.value,
					})),
				});

				if (result) {
					void queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("contacts") });
					void queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes(invitationListKeyItem) });
					void queryClient.invalidateQueries({
						predicate: (query) => query.queryKey.includes("clipping-provider-contacts"),
					});
				}

				return result;
			}}
			onClosed={() => {
				void queryClient.invalidateQueries({ queryKey: contactService.listKey });
				void queryClient.invalidateQueries({ queryKey: contactService.showKey(drawerProps.id) });
				void queryClient.invalidateQueries({ queryKey: birthdayWidgetService.key });
			}}
			validate={validate}
			renderSuccess={(props) => (
				<FormDrawerContent {...props}>
					{props.formik.values.attributes.map((attribute, idx) => (
						<ContactAttributeField
							key={attribute.id}
							attribute={attribute}
							dataFields={dataFields}
							name={`attributes.${idx}.value`}
						/>
					))}
				</FormDrawerContent>
			)}
		/>
	);
};
